import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  auth, 
  actionCodeSettings,
  signInAnonymously, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  googleProvider,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  checkIfEmailExists,
  sendPasswordResetEmail,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential
} from '../firebase';
import { authApi } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function register(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function loginWithGoogle() {
    return signInWithPopup(auth, googleProvider);
  }

  function loginAnonymously() {
    return signInAnonymously(auth);
  }

  function logout() {
    return signOut(auth);
  }

  // Email verification methods
  async function sendVerificationEmail(email) {
    if (!email) {
      throw new Error("Email is required to send verification");
    }
    
    try {
      // Double-check if email exists using the direct Firebase helper
      const emailExists = await checkIfEmailExists(email);
      console.log(`AuthContext - Email existence check for ${email}: ${emailExists}`);
      
      if (emailExists) {
        console.error(`Email ${email} is already registered.`);
        throw new Error("Email already registered. Please use a different email or log in.");
      }
      
      // Store email for sign-in
      localStorage.setItem('emailForSignIn', email);
      
      // Send the verification email
      return await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    } catch (error) {
      console.error("Error in sendVerificationEmail:", error);
      
      // Special handling for Firebase-specific errors
      if (error.code === 'auth/email-already-in-use') {
        throw new Error("Email already registered. Please use a different email or log in.");
      }
      
      throw error; // Re-throw to be handled by the caller
    }
  }

  function completeSignInWithEmailLink(email) {
    if (!email) {
      email = localStorage.getItem('emailForSignIn');
    }
    if (!email) {
      throw new Error("Email is required to complete sign in");
    }
    return signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        localStorage.removeItem('emailForSignIn');
      });
  }

  function isEmailLink(link) {
    return isSignInWithEmailLink(auth, link);
  }

  // Password reset function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Re-authenticate user with password
  function reauthenticate(password) {
    if (!currentUser || !currentUser.email) {
      throw new Error("No user is currently signed in or email is missing");
    }
    
    const credential = EmailAuthProvider.credential(currentUser.email, password);
    return reauthenticateWithCredential(currentUser, credential);
  }

  // Delete user account
  function deleteAccount(password) {
    if (!currentUser) {
      throw new Error("No user is currently signed in");
    }
    
    // If the user is not anonymous, we need to re-authenticate first
    if (!currentUser.isAnonymous) {
      return reauthenticate(password)
        .then(() => {
          return deleteUser(currentUser);
        })
        .then(() => {
          // After successful deletion, sign in anonymously
          return loginAnonymously();
        });
    } else {
      // For anonymous users, just delete directly
      return deleteUser(currentUser)
        .then(() => {
          // After successful deletion, sign in anonymously again
          return loginAnonymously();
        });
    }
  }

  // Fetch admin status from backend
  const fetchAdminStatus = async (user) => {
    if (!user || user.isAnonymous) {
      setIsAdmin(false);
      return;
    }
    
    try {
      const response = await authApi.getCurrentUser();
      if (response.success && response.data) {
        setIsAdmin(response.data.isAdmin || false);
      }
    } catch (error) {
      console.error("Error fetching admin status:", error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user && !user.isAnonymous) {
        fetchAdminStatus(user);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // If no user is logged in, sign in anonymously
  useEffect(() => {
    if (!loading && !currentUser) {
      loginAnonymously()
        .catch(error => {
          console.error("Error signing in anonymously:", error);
        });
    }
  }, [currentUser, loading]);

  const value = {
    currentUser,
    login,
    register,
    loginWithGoogle,
    loginAnonymously,
    logout,
    isAnonymous: currentUser?.isAnonymous,
    isAdmin,
    sendVerificationEmail,
    completeSignInWithEmailLink,
    isEmailLink,
    resetPassword,
    deleteAccount,
    reauthenticate
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 